import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useParams } from 'react-router';

import type { ActivityType, PaymentTerm } from '~/api/common/types';
import type { DocumentFormLine } from '~/features/documents';
import type { routes } from '~/providers/RouterProvider/router.routes';
import type { Params } from '~/providers/RouterProvider/router.types';

import { useClients } from '~/api/clients';
import { useConditions } from '~/api/conditions';
import { useUser } from '~/api/user';
import { CommandMenuItem } from '~/providers/CommandMenuProvider/CommandMenuItem';
import { uuidv4 } from '~/utils/string';

import type { CreateEditInvoiceFormType } from './types';

type Props = {
  form: UseFormReturn<CreateEditInvoiceFormType>;
};

const getRandom = <T,>(array: T[]): T | undefined => array[Math.floor(Math.random() * array.length)];

export const InvoiceFormCommandMenuItems = ({ form: { setValue } }: Props) => {
  const { invoiceId } = useParams<Params<typeof routes.createInvoice | typeof routes.editInvoice>>();

  const { data: clients } = useClients();
  const { data: conditions } = useConditions();
  const { data: user } = useUser();

  return (
    <CommandMenuItem
      group="Invoice"
      label="Fill input fields"
      onClick={() => {
        const options = { shouldDirty: true, shouldTouch: true, shouldValidate: true };

        const title = `Invoice #${invoiceId}`;
        const description = `Generated ${dayjs().format('DD/MM/YY HH:mm:ss')}`;
        const numberingSeriesId =
          getRandom(user.numberingSeries.filter((numberingSeries) => numberingSeries.enabledForInvoices))?.id ?? null;
        const client = getRandom(clients) ?? null;
        const clientId = client?.id ?? null;
        const contactId = getRandom(client?.contacts.filter(({ deleted }) => !deleted) ?? [])?.id ?? null;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const paymentTerm = getRandom([15, 30, 45, 60] satisfies PaymentTerm[])!;
        const brandId = Math.random() > 0.5 ? null : (getRandom(user.brands)?.id ?? null);
        const conditionId = getRandom(conditions)?.id ?? null;
        const lines = Array.from({ length: Math.ceil(Math.random() * 3) }, (_, i) => {
          const activityType: ActivityType =
            i === 0
              ? 'CreativeGeneral'
              : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                getRandom([
                  'CreativeGeneral',
                  'NonCreative',
                  'CreativeReuse',
                  'CreativeSubcontracting',
                  'CreativeComputer',
                ] satisfies ActivityType[])!;

          const price = Math.ceil(Math.random() * 50) * 10;
          const quantity = Math.ceil(Math.random() * 3);

          return {
            uuid: uuidv4(),
            type: 'entry',
            description: `Line ${i + 1}`,
            activityType,
            price,
            discountType: null,
            discountValue: null,
            quantity,
            unit: null,
            productId: null,
          } satisfies DocumentFormLine;
        });
        const sections = [{ name: '', isPhotoAlbum: false, lines }];

        setValue('title', title, options);
        setValue('description', description, options);
        setValue('numberingSeriesId', numberingSeriesId, options);
        setValue('clientId', clientId, options);
        setValue('contactId', contactId, options);
        setValue('paymentTerm', paymentTerm, options);
        setValue('brandId', brandId, options);
        setValue('condition', conditionId ? { id: conditionId, type: 'condition' } : null, options);
        setValue('sections', sections, options);
      }}
    />
  );
};
